import React, { useState, useEffect, useMemo, useRef } from 'react';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { getDoc, doc } from 'firebase/firestore';

import SEO from '../components/shared/SEO';
import ListingCard from '../components/ui/ListingCard';

// ==========================================
// SUB-COMPONENTS
// ==========================================

function ListingSkeleton() {
    return (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="w-full h-48 bg-gray-200 animate-pulse"></div>
            <div className="p-4 space-y-3">
                <div className="w-16 h-4 bg-gray-200 rounded animate-pulse"></div>
                <div className="w-3/4 h-6 bg-gray-200 rounded animate-pulse"></div>
                <div className="w-1/2 h-4 bg-gray-200 rounded animate-pulse mt-2"></div>
                <div className="flex items-center gap-2 mt-4">
                    <div className="w-6 h-6 rounded-full bg-gray-200 animate-pulse"></div>
                    <div className="w-20 h-4 bg-gray-200 rounded animate-pulse"></div>
                </div>
            </div>
        </div>
    );
}

function Calendar({ singleMonth = false, bookedDates, totalInventory, startDate, setStartDate, endDate, setEndDate, currentMonth, setCurrentMonth, selectedOptionName, vacationSettings }) {
    const isDateBooked = (dateString) => {
        const optionToCheck = selectedOptionName || 'default';
        const dailyBookings = bookedDates[dateString] || {};
        const bookedForOption = dailyBookings[optionToCheck] || 0;
        return bookedForOption >= totalInventory;
    };

    const handleDateClick = (date) => {
        const dateString = date.toISOString().split('T')[0];
        if (isDateBooked(dateString)) return;

        let isOnVacation = false;
        if (vacationSettings?.enabled && vacationSettings.startDate && vacationSettings.endDate) {
            if (dateString >= vacationSettings.startDate && dateString <= vacationSettings.endDate) {
                isOnVacation = true;
            }
        }
        if (isOnVacation) return; // Prevent clicking on vacation dates

        if (!startDate || (startDate && endDate)) {
            setStartDate(dateString);
            setEndDate(null);
        } else if (new Date(dateString) < new Date(startDate)) {
            setStartDate(dateString);
        } else {
            setEndDate(dateString);
        }
    };

    const renderCalendar = (monthDate) => {
        const year = monthDate.getFullYear();
        const month = monthDate.getMonth();
        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const days = [];
        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${month}-${i}`} className="w-full h-10"></div>);
        }
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const dateString = date.toISOString().split('T')[0];
            const isBooked = isDateBooked(dateString);
            let isOnVacation = false;
            if (vacationSettings?.enabled && vacationSettings.startDate && vacationSettings.endDate) {
                if (dateString >= vacationSettings.startDate && dateString <= vacationSettings.endDate) {
                    isOnVacation = true;
                }
            }
            const isSelected = (startDate && dateString === startDate) || (endDate && dateString === endDate);
            const isInRange = startDate && endDate && dateString > startDate && dateString < endDate;
            const isPast = date < new Date(new Date().toDateString());

            let classes = "w-10 h-10 flex items-center justify-center rounded-full cursor-pointer ";
            if (isPast || isBooked || isOnVacation) {
                classes += "text-gray-400 line-through cursor-not-allowed bg-gray-100";
            } else if (isSelected) {
                classes += "bg-pink-500 text-white";
            } else if (isInRange) {
                classes += "bg-pink-200";
            } else {
                classes += "hover:bg-pink-100";
            }
            days.push(<div key={`${month}-${day}`} onClick={() => !isPast && !isBooked && !isOnVacation && handleDateClick(date)} className={classes}>{day}</div>);
        }
        return days;
    };

    const nextMonthDate = new Date(currentMonth);
    nextMonthDate.setMonth(currentMonth.getMonth() + 1);

    const handleNextMonth = () => {
        const newDate = new Date(currentMonth);
        newDate.setMonth(newDate.getMonth() + 1);
        setCurrentMonth(newDate);
    };

    const handlePrevMonth = () => {
        const newDate = new Date(currentMonth);
        newDate.setMonth(newDate.getMonth() - 1);
        setCurrentMonth(newDate);
    };

    return (
        <div className="w-full">
            <div className="flex justify-between items-center mb-4">
                <button onClick={handlePrevMonth} className="p-2 rounded-full hover:bg-gray-100 transition-colors">&lt;</button>
                <h3 className="font-bold text-gray-800">{currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}</h3>
                <button onClick={handleNextMonth} className="p-2 rounded-full hover:bg-gray-100 transition-colors">&gt;</button>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-2">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => <div key={d} className="text-center text-xs font-bold text-gray-400 uppercase tracking-wider">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
                {renderCalendar(currentMonth)}
            </div>
            {!singleMonth && (
                <div className="mt-6 border-t border-gray-100 pt-4">
                    <div className="flex justify-center items-center mb-4">
                        <h3 className="font-bold text-gray-800">{nextMonthDate.toLocaleString('default', { month: 'long', year: 'numeric' })}</h3>
                    </div>
                    <div className="grid grid-cols-7 gap-1 mb-2">
                        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => <div key={d} className="text-center text-xs font-bold text-gray-400 uppercase tracking-wider">{d}</div>)}
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                        {renderCalendar(nextMonthDate)}
                    </div>
                </div>
            )}
        </div>
    );
}

// ==========================================
// MAIN COMPONENT
// ==========================================

export default function Listings({ navigateTo, filters, user, db, geocodeAddress, getDistance }) {
    const [listings, setListings] = useState([]);
    const [vendors, setVendors] = useState({});
    const [loading, setLoading] = useState(true);
    const [localFilters, setLocalFilters] = useState(filters || {});
    const [showCalendar, setShowCalendar] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const calendarRef = useRef(null);

    useEffect(() => {
        setLocalFilters(filters || {});
    }, [filters]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                setShowCalendar(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [calendarRef]);

    const handleFilterChange = (key, value) => {
        setLocalFilters(prev => ({...prev, [key]: value}));
    };

    const dateDisplay = () => {
        if (localFilters.startDate && localFilters.endDate) return `${localFilters.startDate} to ${localFilters.endDate}`;
        if (localFilters.startDate) return localFilters.startDate;
        return "Select Dates";
    };

    const parseDate = (dateString) => {
        const [year, month, day] = dateString.split('-').map(Number);
        return new Date(year, month - 1, day);
    };

    const filteredAndSortedListings = useMemo(() => {
        let processedListings = [...listings];

        // Price Range Filter
        if (localFilters.minPrice || localFilters.maxPrice) {
            processedListings = processedListings.filter(l => {
                const min = localFilters.minPrice ? parseFloat(localFilters.minPrice) : 0;
                const max = localFilters.maxPrice ? parseFloat(localFilters.maxPrice) : Infinity;
                return l.price >= min && l.price <= max;
            });
        }

        // Relevance Score for AI Searches
        if (localFilters.aiSearched && localFilters.keywords) {
            const searchTerms = localFilters.keywords.toLowerCase().split(' ').filter(Boolean);
            processedListings.forEach(l => {
                const searchableText = [
                    l.title.toLowerCase(),
                    l.description.toLowerCase(),
                    l.category.toLowerCase(),
                    ...(l.tags || []).map(t => t.toLowerCase())
                ].join(' ');
                
                let score = 0;
                searchTerms.forEach(term => {
                    if (searchableText.includes(term)) score++;
                });
                l.relevanceScore = score;
            });
        }

        // Sorting
        processedListings.sort((a, b) => {
            if (localFilters.aiSearched && (!localFilters.sortBy || localFilters.sortBy === 'popularity')) {
                if ((b.relevanceScore || 0) !== (a.relevanceScore || 0)) {
                    return (b.relevanceScore || 0) - (a.relevanceScore || 0);
                }
            }

            if (localFilters.sortBy === 'price-asc') {
                return a.price - b.price;
            }
            if (localFilters.sortBy === 'price-desc') {
                return b.price - a.price;
            }
            // Default to popularity
            const popA = Object.values(a.bookedDates || {}).reduce((acc, count) => acc + count, 0);
            const popB = Object.values(b.bookedDates || {}).reduce((acc, count) => acc + count, 0);
            return popB - popA;
        });

        return processedListings;
    }, [listings, localFilters]);

    useEffect(() => {
        setLoading(true);
        const q = query(collection(db, "listings"));
        const unsubListings = onSnapshot(q, async (querySnapshot) => {
            let listingsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // --- Fetch vendors for the retrieved listings ---
            if (listingsData.length > 0) {
                const vendorIds = [...new Set(listingsData.map(l => l.vendorId))];
                const vendorsData = {};
                for (const id of vendorIds) {
                    const vendorSnap = await getDoc(doc(db, "users", id));
                    if (vendorSnap.exists()) {
                        vendorsData[id] = vendorSnap.data();
                    }
                }
                setVendors(vendorsData);
            }

            // --- Apply client-side filters ---
            if (localFilters) {
                if (localFilters.keywords) {
                    const searchTerms = localFilters.keywords.toLowerCase().split(' ').filter(Boolean);
                    listingsData = listingsData.filter(l => {
                        const searchableText = [
                            l.title.toLowerCase(),
                            l.description.toLowerCase(),
                            l.category.toLowerCase().replace(/-/g, ' '),
                            ...(l.tags || []).map(t => t.toLowerCase())
                        ].join(' ');
                        
                        if (localFilters.aiSearched) {
                            return searchTerms.some(term => searchableText.includes(term));
                        } else {
                            return searchTerms.every(term => searchableText.includes(term));
                        }
                    });
                }

                if (localFilters.startDate) {
                     listingsData = listingsData.filter(l => {
                        let dateMatch = true;
                        const start = parseDate(localFilters.startDate);
                        const end = localFilters.endDate ? parseDate(localFilters.endDate) : start;

                        let loopDate = new Date(start);
                        while (loopDate <= end) {
                            const dateString = loopDate.toISOString().split('T')[0];
                            if ((l.bookedDates?.[dateString] || 0) >= l.inventory) {
                                dateMatch = false;
                                break;
                            }
                            loopDate.setDate(loopDate.getDate() + 1);
                        }
                        return dateMatch;
                    });
                }

                if (localFilters.category && localFilters.category !== 'all') {
                    listingsData = listingsData.filter(l => l.category === localFilters.category);
                }

                if (localFilters.location && localFilters.radius && geocodeAddress && getDistance) {
                    const searchCoords = await geocodeAddress(localFilters.location);
                    if (searchCoords) {
                        listingsData = listingsData.filter(l => {
                            if (l.coordinates) {
                                const distance = getDistance(searchCoords, l.coordinates);
                                return distance <= localFilters.radius;
                            }
                            return false;
                        });
                    }
                }
            }

            setListings(listingsData);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching listings:", error);
            setLoading(false);
        });

        return () => unsubListings();
    }, [localFilters, db, geocodeAddress, getDistance]);

    if (loading) return (
        <div className="container mx-auto py-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {[1, 2, 3, 4, 5, 6, 7, 8].map(i => <ListingSkeleton key={i} />)}
            </div>
        </div>
    );

    return (
        <div className="container mx-auto">
            <SEO 
                title="Browse Party Rentals" 
                description="Search for party supplies, venues, and services. Filter by category, price, and location." 
            />
            {localFilters.searchMessage && (
                <div className="bg-pink-50 border border-pink-100 text-pink-800 p-4 rounded-xl mb-8 text-center shadow-sm">
                    <p className="font-semibold text-lg">{localFilters.searchMessage}</p>
                </div>
            )}
            <h2 className="text-3xl font-bold mb-6 text-gray-900">Party Supplies For You</h2>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 mb-8 flex flex-wrap gap-4 items-center">
                <div className="flex items-center gap-3">
                    <label className="font-semibold text-sm text-gray-700">Sort By:</label>
                    <select value={localFilters.sortBy || 'popularity'} onChange={e => handleFilterChange('sortBy', e.target.value)} className="p-2.5 border-gray-200 rounded-lg text-sm focus:ring-pink-500 focus:border-pink-500 bg-gray-50">
                        <option value="popularity">Popularity</option>
                        <option value="price-asc">Price: Low to High</option>
                        <option value="price-desc">Price: High to Low</option>
                    </select>
                </div>
                <div className="flex items-center gap-2">
                    <label className="font-semibold">Price:</label>
                    <input type="number" placeholder="Min $" value={localFilters.minPrice || ''} onChange={e => handleFilterChange('minPrice', e.target.value)} className="w-24 p-2 border rounded-md" />
                    <span>-</span>
                    <input type="number" placeholder="Max $" value={localFilters.maxPrice || ''} onChange={e => handleFilterChange('maxPrice', e.target.value)} className="w-24 p-2 border rounded-md" />
                </div>
                <div className="flex items-center gap-2">
                    <label className="font-semibold">Category:</label>
                    <select value={localFilters.category || 'all'} onChange={e => handleFilterChange('category', e.target.value)} className="p-2 border rounded-md">
                        <option value="all">All Categories</option>
                        <option value="venues">Venues</option>
                        <option value="tents-canopies">Tents & Canopies</option>
                        <option value="tables-chairs">Tables & Chairs</option>
                        <option value="decorations">Decorations</option>
                        <option value="djs">DJs</option>
                        <option value="entertainment">Entertainment</option>
                        <option value="food">Food & Catering</option>
                        <option value="mobile-bars">Mobile Bars</option>
                        <option value="florals">Florals</option>
                        <option value="bakery">Bakery & Cakes</option>
                        <option value="photography">Photography</option>
                        <option value="videography">Videography</option>
                        <option value="content-creation">Content Creation</option>
                        <option value="audio-visual">Audio/Visual</option>
                        <option value="staffing">Event Staffing</option>
                        <option value="transportation">Transportation</option>
                        <option value="mobile-restrooms">Mobile Restrooms</option>
                        <option value="packages">Packages</option>
                        <option value="planners">Event Planners</option>
                    </select>
                </div>
                <div className="relative">
                    <button onClick={() => setShowCalendar(!showCalendar)} className="p-2 border rounded-md bg-white text-gray-700">
                        {dateDisplay()}
                    </button>
                    {showCalendar && (
                        <div ref={calendarRef} className="absolute top-full mt-2 bg-white p-4 rounded-lg shadow-2xl z-20 w-[320px] md:w-[660px] left-0">
                            <Calendar
                                singleMonth={false}
                                bookedDates={{}}
                                totalInventory={1}
                                startDate={localFilters.startDate}
                                setStartDate={(date) => handleFilterChange('startDate', date)}
                                endDate={localFilters.endDate}
                                setEndDate={(date) => handleFilterChange('endDate', date)}
                                currentMonth={currentMonth}
                                setCurrentMonth={setCurrentMonth}
                            />
                        </div>
                    )}
                </div>
            </div>

            {filteredAndSortedListings.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {filteredAndSortedListings.map(listing => (
                        <ListingCard 
                            key={listing.id} 
                            listing={listing} 
                            vendor={vendors[listing.vendorId]} 
                            navigateTo={navigateTo} 
                            user={user} 
                            isFavorite={user && !user.isAnonymous && user.favorites?.includes(listing.id)} 
                        />
                    ))}
                </div>
            ) : (
                <div className="text-center py-16 bg-white rounded-2xl shadow-sm border border-gray-100">
                    <div className="text-4xl mb-4">🔍</div>
                    <h3 className="text-xl font-bold mb-2 text-gray-800">No items found</h3>
                    <p className="text-gray-500">Try adjusting your search, category, or dates to see more results.</p>
                    <button onClick={() => handleFilterChange('category', 'all')} className="mt-6 px-6 py-2 bg-pink-50 text-pink-600 rounded-full font-bold hover:bg-pink-100 transition">Clear Filters</button>
                </div>
            )}
        </div>
    );
}